import { useState, useEffect, useMemo } from 'react';

import { BalanceBar } from '@/components/ui/BalanceBar';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { CardRenderer } from '@/components/Cards/CardRenderer';
import { FilterModal } from '@/components/Modals/FilterModal';
import { ExperimentalFilterBar } from '@/components/ui/ExperimentalFilterBar';
import { SectionListView } from '@/components/ui/SectionListView';
import { usernameMarketApi } from '@/utils/api';
import { socket } from '@/utils/socket';
import { getCached, setCached, isCacheFresh } from '@/utils/cache';
import { resolveListingCategory } from '@/utils/kindResolver';
import { FilterPanel, PricePanel } from '@/components/ui/FilterPanel';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';
import type { NamedFilter } from '@/components/ui/ExperimentalFilterBar';
import {
    Clock, Activity, Gavel,
    AtSign, Gem, Wallet, Store as StoreIcon, Users, Crown, ArrowUpDown, Flame
} from 'lucide-react';

const AUCTION_FILTERS: NamedFilter[] = [
    { id: 'all', label: 'All', icon: <Users className="w-3 h-3 text-white/50" /> },
    { id: 'live', label: 'Live', icon: <Flame className="w-3 h-3 text-red-400" /> },
    { id: 'upcoming', label: 'Upcoming', icon: <Clock className="w-3 h-3 text-amber-300" /> },
    { id: 'ending_soon', label: 'Ending Soon', icon: <Activity className="w-3 h-3 text-orange-400" /> },
    { id: 'ended', label: 'Ended', icon: <Crown className="w-3 h-3 text-white/40" /> },
    { id: 'usernames', label: 'Usernames', icon: <AtSign className="w-3 h-3 text-sky-300" /> },
    { id: 'nfts', label: 'NFTs', icon: <Gem className="w-3 h-3 text-purple-400" /> },
    { id: 'start_price', label: 'Starting Price', icon: <Wallet className="w-3 h-3 text-emerald-300" /> },
    { id: 'current_bid', label: 'Current Bid', icon: <Wallet className="w-3 h-3 text-lime-300" /> },
    { id: 'seller', label: 'Seller', icon: <StoreIcon className="w-3 h-3 text-blue-300" /> },
    { id: 'sort', label: 'Sort By', icon: <ArrowUpDown className="w-3 h-3 text-white/60" /> },
];

const HOURS_LEFT = (d: any) => {
    const end = d?.ends_at || d?.endTime;
    if (!end) return Infinity;
    const t = new Date(end).getTime();
    return (t - Date.now()) / 3_600_000;
};


interface AuctionsProps {
    onNavigate: (page: string, category?: string) => void;
    onSearch?: () => void;
    isActive?: boolean;
}

const Auctions = ({ onNavigate, onSearch, isActive = true }: AuctionsProps) => {
    const { } = useHapticFeedback();
    const [finishedAuctions, setFinishedAuctions] = useState<any[]>([]);
    const [umktAuctions, setUmktAuctions] = useState<any[]>([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [quickFind, setQuickFind] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'compact' | 'activity'>('grid');
    const [activeFilter, setActiveFilter] = useState('all');
    const [sellerIds, setSellerIds] = useState<string[]>([]);
    const [priceMeasure, setPriceMeasure] = useState<'current' | 'start'>('current');
    const [priceMin, setPriceMin] = useState<number | null>(null);
    const [priceMax, setPriceMax] = useState<number | null>(null);
    const [sortKey, setSortKey] = useState('ending');
    const [sellerPanelOpen, setSellerPanelOpen] = useState(false);
    const [pricePanelOpen, setPricePanelOpen] = useState(false);
    const [sortPanelOpen, setSortPanelOpen] = useState(false);

    const toggleFilter = (id: string) => {
        if (id === 'all') {
            setActiveFilter('all');
            setSellerIds([]);
            setPriceMin(null);
            setPriceMax(null);
            setPriceMeasure('current');
            setSortKey('ending');
            return;
        }
        if (id === 'start_price') { setPriceMeasure('start'); setPricePanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'current_bid') { setPriceMeasure('current'); setPricePanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'seller') { setSellerPanelOpen(true); setActiveFilter('all'); return; }
        if (id === 'sort') { setSortPanelOpen(true); setActiveFilter('all'); return; }
        setActiveFilter(prev => (prev === id ? 'all' : id));
    };

    const loadUmktAuctions = async () => {
        try {
            const res = await usernameMarketApi.market('auction');
            const d = await res.json();
            if (d.success && Array.isArray(d.listings) && d.listings.length > 0) {
                setUmktAuctions(d.listings);
                setCached('cached_umkt_auctions', d.listings);
            }
        } catch (e) { console.error('[auctions] username-market load failed', e); }
    };

    useEffect(() => {
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

        if (isActive) {
            loadUmktAuctions();
        }

        // ─── Polling: 30s — Socket.IO handles instant updates ───
        const interval = setInterval(() => { loadUmktAuctions(); }, 30_000);

        // Finished auctions (cached 5 min)
        const cachedFinished = getCached<any[]>('finished_auctions', 300000);
        if (cachedFinished) {
            setFinishedAuctions(cachedFinished);
        }
        if (!isCacheFresh('finished_auctions', 300000)) {
            // username_market does not expose finished yet — keep cached copy only
        }

        return () => clearInterval(interval);
    }, [isActive]);

    // Real-time instant socket updates for Auctions
    useEffect(() => {
        const handleRefresh = () => {
            loadUmktAuctions();
        };

        socket.on('market_update', handleRefresh);
        socket.on('push_usernames_updated', handleRefresh);
        socket.on('auction_update', handleRefresh);

        return () => {
            socket.off('market_update', handleRefresh);
            socket.off('push_usernames_updated', handleRefresh);
            socket.off('auction_update', handleRefresh);
        };
    }, []);

    // Map username_market data to UI format
    const auctions = useMemo(() => {
        let list: any[] = [];

        // ─── username_market auctions (single source of truth) ───
        list = umktAuctions
            .filter((a: any) => a.sale_type === 'auction' && a.listing_status === 'active')
            .map((a: any) => {
            const price = a.current_bid ? parseFloat(a.current_bid) : (a.start_price ? parseFloat(a.start_price) : parseFloat(a.ask_price) || 1);
            const endDt = a.ends_at ? new Date(a.ends_at).getTime() : null;
            const photo = a.seller_avatar || a.owner_avatar || a.avatarUrl || a.photo_url || a.photoUrl || '/icons/default-avatar.svg';
            const ownerLabel = a.seller_name || a.seller_first_name || (a.username ? `@${a.username}` : String(a.seller_id));
            return {
                ...a,
                id: `umkt_${a.listing_id}`,
                auctionId: `umkt_${a.listing_id}`, // umkt_ prefix → AuctionBid routes to username-market
                umktId: a.listing_id,
                target_username: a.username || `ITEM_${a.listing_id}`,
                ownerName: ownerLabel,
                displayName: a.username ? `@${a.username}` : 'User', // Main Title
                verified_username: a.username,
                username: a.username,
                price, // Current price: highest bid or starting price
                profileImage: photo,
                waveColor: '#3390EC',
                textColor: '#FFFFFF',
                likesCount: 0,
                isLiked: false,
                type: 'AUCTION' as const,
                isNft: a.asset_type === 'nft',
                assetType: a.asset_type || 'ownership',
                seller_id: a.seller_id,
                seller_first_name: a.seller_first_name || a.seller_name,
                seller_last_name: a.seller_last_name || '',
                seller_name: ownerLabel,
                seller_username: a.seller_username || a.username,
                owner_name: ownerLabel,
                avatarUrl: photo,
                ownerAvatar: photo,
                ownerImage: photo,
                seller_avatar: photo,
                owner_avatar: photo,
                showTimer: true,
                timerHours: endDt ? Math.max(0.01, (endDt - Date.now()) / 3_600_000) : 24,
                endTime: a.ends_at,
                currentBid: price,
                minBidStep: parseFloat(a.min_increment) || 10,
                bidCount: Number(a.bid_count) || 0,
                isActiveAuction: true,
                isPending: !a.current_bid, // Start on First Bid
                isV2: false,
                isUmkt: true
            };
        });

        // De-duplicate by username or ID and strictly filter AUCTION items
        const seen = new Set();
        let uniqueList = list.filter((item: any) => {
            if (resolveListingCategory(item) !== 'auction') return false;
            const cleanName = String(item.username || item.target_username || item.displayName || item.name || '').toLowerCase().replace(/^@/, '');
            const key = cleanName ? `u:${cleanName}` : `id:${item.id || item.auctionId}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        if (quickFind.trim()) {
            const query = quickFind.toLowerCase();
            uniqueList = uniqueList.filter(a => String(a.displayName || '').toLowerCase().includes(query) || String(a.username || '').toLowerCase().includes(query));
        }

        return uniqueList;
    }, [umktAuctions, quickFind]);

    const finished = useMemo(() => {
        return finishedAuctions.map((a: any) => ({
            ...a,
            id: a.id,
            target_username: a.target_username || a.card_username || a.card_name || a.username || a.name,
            displayName: a.seller_name ? `@${a.seller_name}` : 'User',
            price: parseFloat(a.current_bid),
            profileImage: a.image_filename
                ? (a.image_filename.startsWith('/card_Image') ? a.image_filename : `/card_Image/${a.image_filename}`)
                : undefined,
            waveColor: a.base_color || '#3390EC',
            textColor: a.text_color || '#FFFFFF',
            ownerImage: a.seller_avatar,
            badge: "ENDED",
            hideBuyButton: true,
            isSold: true,
            type: 'AUCTION'
        }));
    }, [finishedAuctions]);

    // Derived Lists
    // @ts-ignore
    const tgUser: any = window.Telegram?.WebApp?.initDataUnsafe?.user || {};

    const isListView = viewMode === 'list' || viewMode === 'compact';

    // Unified grid catalog — dedupe by id so no card is repeated
    const soldIds = useMemo(() => new Set(finished.map(i => String(i.id ?? i.auctionId ?? i.auction_id))), [finished]);
    const auctionGridItems = useMemo(() => {
        const seen = new Set<string>();
        const out: any[] = [];
        [...finished, ...auctions].forEach(it => {
            const key = String(it.auctionId ?? it.auction_id ?? it.id);
            if (key && !seen.has(key)) { seen.add(key); out.push(it); }
        });
        return out;
    }, [finished, auctions]);

    // ─── New Filter & Sort Pipeline ───
    const sellerOptions = useMemo(() => {
        const m = new Map<string, { id: string; label: string }>();
        auctionGridItems.forEach((i: any) => {
            const id = String(i.seller_id ?? '');
            if (!id || id === '0') return;
            if (!m.has(id)) m.set(id, { id, label: i.seller_name || i.seller_first_name || i.seller_username || `#${id}` });
        });
        return Array.from(m.values());
    }, [auctionGridItems]);

    const filteredAuctions = useMemo(() => {
        let out = [...auctionGridItems];

        if (activeFilter === 'usernames') out = out.filter(i => !i.isNft && String(i.assetType || i.asset_type || '').toLowerCase() !== 'nft');
        else if (activeFilter === 'nfts') out = out.filter(i => i.isNft || String(i.assetType || i.asset_type || '').toLowerCase() === 'nft');
        else if (activeFilter === 'live') out = out.filter(i => !soldIds.has(String(i.auctionId ?? i.auction_id ?? i.id)) && HOURS_LEFT(i) > 0);
        else if (activeFilter === 'upcoming') out = out.filter(i => {
            const s = i.starts_at ? new Date(i.starts_at).getTime() : null;
            return s != null && s > Date.now();
        });
        else if (activeFilter === 'ending_soon') out = out.filter(i => {
            const h = HOURS_LEFT(i);
            return h > 0 && h <= 24;
        });
        else if (activeFilter === 'ended') out = out.filter(i => {
            if (soldIds.has(String(i.auctionId ?? i.auction_id ?? i.id))) return true;
            const h = HOURS_LEFT(i);
            return h <= 0;
        });

        if (sellerIds.length > 0) out = out.filter(i => sellerIds.includes(String(i.seller_id ?? '')));
        if (priceMin != null || priceMax != null) {
            out = out.filter(i => {
                const p = priceMeasure === 'start'
                    ? Number(i.start_price ?? i.starting_price ?? i.price ?? 0)
                    : Number(i.current_bid ?? i.price ?? 0);
                if (priceMin != null && p < priceMin) return false;
                if (priceMax != null && p > priceMax) return false;
                return true;
            });
        }

        if (quickFind.trim()) {
            const q = quickFind.toLowerCase();
            out = out.filter(i => String(i.username || i.displayName || '').toLowerCase().includes(q));
        }

        if (sortKey === 'ending') out.sort((a, b) => HOURS_LEFT(a) - HOURS_LEFT(b));
        else if (sortKey === 'price_asc') out.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
        else if (sortKey === 'price_desc') out.sort((a, b) => Number(b.price ?? 0) - Number(a.price ?? 0));
        else if (sortKey === 'newest') out.sort((a, b) => Number(b.listing_id ?? a.id ?? 0) - Number(a.listing_id ?? b.id ?? 0));
        return out;
    }, [auctionGridItems, activeFilter, sellerIds, priceMin, priceMax, priceMeasure, sortKey, quickFind, soldIds]);

    // List mode groups — deduped so nothing repeats
    const listFinished = useMemo(() => {
        const seen = new Set<string>();
        const base = filteredAuctions.filter(it => soldIds.has(String(it.auctionId ?? it.auction_id ?? it.id)));
        return base.filter(it => {
            const k = String(it.auctionId ?? it.auction_id ?? it.id);
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }, [filteredAuctions, soldIds]);

    const listOther = useMemo(() => {
        const seen = new Set<string>(listFinished.map(i => String(i.auctionId ?? i.auction_id ?? i.id)));
        const out: any[] = [];
        filteredAuctions.forEach(it => {
            const k = String(it.auctionId ?? it.auction_id ?? it.id);
            if (k && !seen.has(k)) { seen.add(k); out.push(it); }
        });
        return out;
    }, [listFinished, filteredAuctions]);

    return (
        <div className="pt-4 min-h-screen pb-24 bg-tg-bg text-tg-text">
            <div className="px-4 mb-2">
                <BalanceBar
                    onDepositClick={() => onNavigate('wallet', 'deposit')}
                    onHistoryClick={() => onNavigate('p2p_history')}
                    logoVariant="lottie"
                    logoActive={isActive}
                    logoIcon={<Gavel size={20} className="text-amber-300" />}
                />
            </div>

            {/* EXPERIMENTAL SEARCH & FILTER BAR SECTION */}
            <ExperimentalFilterBar
                quickFind={quickFind}
                onQuickFindChange={(val) => setQuickFind(val)}
                onViewModeChange={setViewMode}
                viewMode={viewMode}
                items={[...finished, ...auctions]}
                filters={AUCTION_FILTERS}
                activeFilter={activeFilter}
                onFilterSelect={toggleFilter}
                onPurchasesClick={() => onNavigate('purchases')}
                onFilterApply={(ids) => onNavigate('all_items', 'Filtered')}
            />

            <FilterModal 
                isOpen={isFilterOpen} 
                onClose={() => setIsFilterOpen(false)} 
                onApply={(filters) => {
                    console.log('Applied filters:', filters);
                    onNavigate('all_items', filters.category || 'Filtered');
                }}
            />


            {/* RECENTLY FINISHED (New Section) */}
            {listFinished.length > 0 && isListView && (
                <SectionListView
                    title=""
                    icon={null}
                    items={listFinished}
                    sectionId="finished"
                    isSold
                    forceAuction
                    onItemClick={(auction) => {
                        const targetId = String(auction.auctionId || auction.id || (auction.umktId ? `umkt_${auction.umktId}` : ''));
                        if (targetId) localStorage.setItem('selected_auction_id', targetId);
                        onNavigate('auction_bid', auction);
                    }}
                />
            )}

            {listOther.length > 0 && isListView && (
                <SectionListView
                    title=""
                    icon={null}
                    items={listOther}
                    sectionId="live"
                    forceAuction
                    onItemClick={(auction) => {
                        const targetId = String(auction.auctionId || auction.id || (auction.umktId ? `umkt_${auction.umktId}` : ''));
                        if (targetId) localStorage.setItem('selected_auction_id', targetId);
                        onNavigate('auction_bid', auction);
                    }}
                />
            )}

            {!isListView && filteredAuctions.length > 0 && (
                <div className="px-4 grid grid-cols-2 gap-2 transform-gpu" style={{ transform: 'translateZ(0)' }}>
                    {filteredAuctions.map((item: any) => {
                        const key = String(item.auctionId ?? item.auction_id ?? item.id);
                        const isSoldCard = soldIds.has(key);
                        const handleAuctionClick = () => {
                            const targetId = String(item.auctionId || item.id || (item.umktId ? `umkt_${item.umktId}` : ''));
                            if (targetId) localStorage.setItem('selected_auction_id', targetId);
                            onNavigate('auction_bid', item);
                        };
                        return (
                            <CardRenderer
                                key={`auct-grid-${key}`}
                                {...item}
                                hideBuyButton={isSoldCard}
                                variant="premium"
                                scale={0.55}
                                showCardIcon={true}
                                compactOwner={true}
                                badge={isSoldCard ? (item.badge || 'SOLD') : (item.isNft || item.assetType === 'nft' ? 'NFT' : undefined)}
                                onBuy={handleAuctionClick}
                                onInteract={handleAuctionClick}
                                onClick={handleAuctionClick}
                            />
                        );
                    })}
                </div>
            )}

            {filteredAuctions.length === 0 && (() => {
                const f = (activeFilter || '').toLowerCase();
                const meta = (
                    f === 'live' || f === 'ending_soon' ? { emojiId: '107', title: 'No live auctions', desc: 'New auctions appear here as sellers list them.' } :
                    f === 'upcoming' ? { emojiId: '108', title: 'No upcoming auctions', desc: 'Auctions scheduled for later will show here.' } :
                    f === 'ended' ? { emojiId: '106', title: 'No ended auctions', desc: 'Past completed auctions appear here.' } :
                    f === 'usernames' ? { emojiId: '111', title: 'No username auctions', desc: 'No active username auctions match your filter.' } :
                    f === 'nfts' ? { emojiId: '98', title: 'No NFT auctions', desc: 'No active NFT auctions matching your filter.' } :
                    f.includes('price') || f === 'start_price' || f === 'current_bid' ? { emojiId: '101', title: 'No auctions in price range', desc: 'Try adjusting your min and max price.' } :
                    f === 'seller' ? { emojiId: '87', title: 'No auctions from seller', desc: 'No active auctions found for this seller.' } :
                    f === 'sort' ? { emojiId: '110', title: 'No auctions matching sort', desc: 'Try a different sorting option.' } :
                    { emojiId: '107', title: 'No matching auctions', desc: 'Try adjusting your search or clear your filters.' }
                );

                return (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <div className="w-24 h-24 mb-3 flex items-center justify-center">
                            <CustomEmojiPlayer emojiId={meta.emojiId} size={92} />
                        </div>
                        <p className="text-white font-extrabold text-base mb-1">{meta.title}</p>
                        <p className="text-[12px] text-white/40 max-w-[250px] mb-4">{meta.desc}</p>
                        <button onClick={() => { setActiveFilter('all'); setSellerIds([]); setPriceMin(null); setPriceMax(null); }}
                            className="px-5 py-2.5 rounded-2xl bg-white/10 text-white text-sm font-bold hover:bg-white/15 active:scale-95 transition-all">
                            Clear filters
                        </button>
                    </div>
                );
            })()}

            <FilterPanel
                open={sellerPanelOpen}
                title="Filter by Seller"
                icon={<StoreIcon className="w-4 h-4 text-blue-400" />}
                options={sellerOptions}
                selected={sellerIds}
                searchable
                onToggle={(id) => setSellerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                onClose={() => setSellerPanelOpen(false)}
                onClear={() => setSellerIds([])}
            />

            <PricePanel
                open={pricePanelOpen}
                title={`Filter by ${priceMeasure === 'start' ? 'Starting Price' : 'Current Bid'} (GRAM)`}
                min={priceMin}
                max={priceMax}
                onMinChange={setPriceMin}
                onMaxChange={setPriceMax}
                onClose={() => setPricePanelOpen(false)}
                onClear={() => { setPriceMin(null); setPriceMax(null); }}
                onApply={() => {}}
            />

            <FilterPanel
                open={sortPanelOpen}
                title="Sort By"
                icon={<ArrowUpDown className="w-4 h-4 text-white/60" />}
                options={[
                    { id: 'ending', label: 'Ending Soonest' },
                    { id: 'newest', label: 'Newest' },
                    { id: 'price_asc', label: 'Price: Low to High' },
                    { id: 'price_desc', label: 'Price: High to Low' },
                ]}
                selected={[sortKey]}
                singleSelect
                onToggle={(id) => setSortKey(id || 'ending')}
                onClose={() => setSortPanelOpen(false)}
                onClear={() => setSortKey('ending')}
            />

        </div>
    );
};

export default Auctions;